/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

'use strict';

import * as angular from 'angular';
import { LegacyRouting } from 'src/app/services/legacy-helpers/legacy-routing.service';

import { ProjectNotifier } from 'src/app/services/project-notifier.service';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import firebase from 'firebase/compat/app';

declare const window: any;

angular
	.module('ticketspaceApp')
	.factory('firebaseMeshedUsers', [
		'$q',
		'$firebaseUtils',
		'$firebaseArray',
		'legacyRouting',
		'fbConnection',
		function ($q, $firebaseUtils, $firebaseArray, legacyRouting: LegacyRouting, fbConnection) {
			const logger = LoggerServiceFactory.create('firebaseMeshedUsers');
			//TODO - continue testing this at various level
			// - maybe consider mapping some things into $$update...somehow, or not

			//consider adding objectMapping support for keys (to support duplicates)
			let supportedKeys = {
				users: ['accountId', 'email', 'company', 'phone', 'firstName', 'lastName', 'zip', 'isApi'],
				projectMembers: ['accessLevel', 'roleId', 'secondaryRoles', 'isApi'],
				roles: ['roleName', 'roleStyle'],
				accounts: ['tier', 'trialDays'],
			};

			//list of decendents more then one level deep, used to off things when a high level node loses access
			let deepDecendents = {
				projectMembers: ['roles'],
			};

			function orderByNameComparator(a, b) {
				// If we don't get strings, just compare by index
				/*
    if (a.type !== 'string' || b.type !== 'string') {
      return (a.index < b.index) ? -1 : 1;
    }
		logger.log('a',a);
		return a.value.toLowerCase().localeCompare(b.value.toLowerCase());
		*/
				if (!a.data.formattedName && !b.data.formattedName) {
					return 0;
				}
				if (!a.data.formattedName) {
					return 1;
				}
				if (!b.data.formattedName) {
					return -1;
				}

				return a.data.formattedName.toLowerCase().localeCompare(b.data.formattedName.toLowerCase());
			}

			function formatName(topObj) {
				let obj = topObj.data;
				if (obj.firstName || obj.lastName) {
					obj.formattedName = (obj.firstName + ' ' || '') + (obj.lastName || '');
				} else if (obj.email) {
					obj.formattedName = obj.email;
				} else {
					obj.formattedName = 'n/a';
				}
			}

			//prejectId required, config can optionally be used to spec an alternative project path
			//maybe add support to override everything in baseRefCollection as well
			function MeshFactory(projectId, config) {
				config = config || {};
				const projectPath = fancyFirebaseGetters.getProjectsPath(projectId);
				const projectMembersPath = fancyFirebaseGetters.getProjectMembersPath(projectId);
				const rolesPath = fancyFirebaseGetters.getRolesPath(projectId);
				const accountsPath = fancyFirebaseGetters.getAccountsPath();

				let ref = fancyFirebaseReferencer
					.getLegacyRef<firebase.database.Reference>(projectPath, projectId)
					.child('users');

				this.projectId = projectId;
				this.baseRefCollection = {
					accounts: fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(accountsPath),
					users: fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>('users', projectId),
					userIndex: fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
						'indices/users',
						projectId
					),
					projectMembers: fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
						projectMembersPath,
						projectId
					),
					roles: fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(rolesPath, projectId),
				};
				this.projectMemberIdx = {};

				// call the super constructor
				let thing = $firebaseArray.call(this, ref);
				return thing;
			}
			MeshFactory.prototype = {
				$$added(snap) {
					return this.buildInitial(snap);
				},
				buildInitial(userIdSnap) {
					let self = this;
					return new $q(function (resolve, reject) {
						if (!userIdSnap.exists()) {
							reject(new Error("this shouldn't be possible"));
							return;
						}

						//object to be returned
						let obj: any = { data: {}, $snaps: {} };
						//the callbacks to be used in offing
						let cbs: any = {};
						//store the refs, again, for offing
						let refs: any = {};

						//setup...
						let userId = userIdSnap.key;
						obj.$id = userId;
						obj.data.userId = userId;
						obj.$snaps['userId'] = userIdSnap;
						obj.$refs = refs;
						obj.$cbs = cbs;

						//some rather ugly crap to resolve the initial add only once everythings loads as far as it can go
						let resolvedCount = 0;
						let firstUsers = true;
						let firstProjectMembers = true;
						function tryResolve() {
							resolvedCount++;
							//logger.log('tryResolve', resolvedCount);
							if (resolvedCount >= 2) {
								resolve(obj);
							}
						}

						//#### doUpdate(snap, supportedKeys)
						// manages the updates into/ out of the obj
						function doUpdate(snap, supportedKeys) {
							let newObj;
							self.$list.$lastUpdated = Date.now();
							if (!snap || !snap.exists()) {
								newObj = {};
							} else {
								newObj = snap.val();
							}

							supportedKeys.forEach(function (key) {
								if (newObj[key] !== undefined && newObj[key] !== null) {
									obj.data[key] = newObj[key];
								} else {
									delete obj.data[key];
								}
							});
						}

						//#### updateDescendent(snap, idxKey, descendentKey)
						// handles the cleanup and reindexing when an id changes
						function updateDescendent(snap, idxKey, descendentKey) {
							//reset things
							if (refs[descendentKey]) {
								refs[descendentKey].off('value', cbs[descendentKey]);
								let deep = deepDecendents[descendentKey];
								if (deep) {
									deep.forEach(function (d) {
										if (refs[d]) {
											refs[d].off('value', cbs[d]);
											doUpdate(null, supportedKeys[d]);
										}
									});
								}
							}
							//logger.log('a', descendentKey,supportedKeys[descendentKey] );
							doUpdate(null, supportedKeys[descendentKey]);

							let idx = idxKey ? snap.child(idxKey).val() : snap.val();
							if (idx) {
								refs[descendentKey] = self.baseRefCollection[descendentKey].child(idx);
								refs[descendentKey].on('value', cbs[descendentKey]);
							}

							return idx;
						}

						//callbacks

						//users callback
						cbs['users'] = function usersValueCb(userSnap) {
							let accountNeedsUpdate = obj.data.accountId !== userSnap.child('accountId').val();
							doUpdate(userSnap, supportedKeys.users);
							obj.$snaps['users'] = userSnap;

							formatName(obj);

							if (accountNeedsUpdate) {
								if (!updateDescendent(userSnap, 'accountId', 'accounts')) {
									//first run
									if (firstUsers) {
										tryResolve();
									}
									firstUsers = false;
								}
							}
						};
						//acounts callback
						cbs['accounts'] = function accountsValueCb(accountSnap) {
							//logger.log('account things', accountSnap.val(), accountSnap.ref.toString());
							doUpdate(accountSnap, supportedKeys.accounts);
							obj.$snaps['accounts'] = accountSnap;
							//first run
							if (firstUsers) {
								tryResolve();
							}
							firstUsers = false;
						};
						//userIndex callback
						cbs['userIndex'] = function userIndexValueCb(pmIndexSnap) {
							let pmChanged = pmIndexSnap.val() !== obj.data.projectMemberId;
							obj.$snaps['userIndex'] = pmIndexSnap;
							obj.data.projectMemberId = pmIndexSnap.val();

							if (pmChanged) {
								if (!updateDescendent(pmIndexSnap, null, 'projectMembers')) {
									//first run
									if (firstProjectMembers) {
										tryResolve();
									}
									firstProjectMembers = false;
								}
							}
						};
						//projectMembers callback
						cbs['projectMembers'] = function projectMembersValueCb(pmSnap) {
							let roleChanged = pmSnap.child('roleId').val() !== obj.data.roleId;
							doUpdate(pmSnap, supportedKeys.projectMembers);
							obj.$snaps['projectMembers'] = pmSnap;
							self.$$notify('child_changed', userId);

							if (roleChanged) {
								if (!updateDescendent(pmSnap, 'roleId', 'roles')) {
									//first run
									if (firstProjectMembers) {
										tryResolve();
									}
									firstProjectMembers = false;
								}
							}
						};
						//roles callback
						cbs['roles'] = function rolesValueCb(roleSnap) {
							doUpdate(roleSnap, supportedKeys.roles);
							obj.$snaps['roles'] = roleSnap;

							if (firstProjectMembers) {
								tryResolve();
							}
							firstProjectMembers = false;
						};

						//users callback
						refs['users'] = self.baseRefCollection.users.child(userId);
						refs['users'].on('value', cbs['users']);

						refs['userIndex'] = self.baseRefCollection.userIndex
							.child(userIdSnap.key)
							.child('projectMember')
							.child(self.projectId)
							.child('projectMemberId');
						refs['userIndex'].on('value', cbs['userIndex']);
					});
				},
				$$updated(snap) {
					//in theory the "value" this is actually worthless, so just override this to do nothing
				},
				$$removed(snap) {
					let rec = this.$getRecord(snap.key);
					if (rec) {
						for (let key in rec.refs) {
							rec.refs[key].off('value', rec.cbs[key]);
						}
					}
					return $firebaseArray.prototype.$$removed.apply(this, arguments);
					// this.updateUsedStyles('remove', snap, snap);
					// return this.$getRecord(snap.key);
				},
				$$process(type, rec, prevChild) {
					if (type === 'child_removed') {
						//logger.log('removed', this.$indexFor(this.$$getKey(rec)), rec);
						delete this.projectMemberIdx[rec.data.projectMemberId];
					}

					let changed = $firebaseArray.prototype.$$process.apply(this, arguments);

					if (type === 'child_added') {
						//logger.log('added', this.$indexFor(this.$$getKey(rec)), rec);
						let key = this.$$getKey(rec);
						let i = this.$indexFor(key);
						this.projectMemberIdx[key] = i;
					}

					return changed;
				},
				$pmIndexFor(key) {
					//logger.log('pmKey', key, this.projectMemberIdx);
					let self = this;
					let cache = self.projectMemberIdx;
					// evaluate whether our key is cached and, if so, whether it is up to date
					if (
						!cache.hasOwnProperty(key) ||
						!self.$list[key] ||
						!self.$list[key].data.projectMemberId !== key
					) {
						// update the hashmap
						let pos = self.$list.findIndex(function (rec) {
							return rec.data.projectMemberId === key;
						});
						//logger.log('pos', pos);
						if (pos !== -1) {
							cache[key] = pos;
						}
					}
					//logger.log('cache', cache, key, cache[key]);
					return cache.hasOwnProperty(key) ? cache[key] : -1;
				},
				$getPmRecord(key) {
					let i = this.$pmIndexFor(key);
					//logger.log('this.$list', i, this.$list, this.$list[i]);
					return i > -1 ? this.$list[i] : null;
				},
				$destroy() {
					if (this.$list) {
						this.$list.forEach(function (rec) {
							for (let key in rec.refs) {
								rec.refs[key].off('value', rec.cbs[key]);
							}
						});
					}
					return $firebaseArray.prototype.$destroy.apply(this, arguments);
				},
				//slap utility functions directly onto the collection
				orderByNameComparator,
			};

			let eFactory = $firebaseArray.$extend(MeshFactory);

			//add utility functions
			eFactory.orderByNameComparator = orderByNameComparator;

			return eFactory;
		},
	])
	//figure out how to keep this updated with project changes
	.factory('firebaseMeshedUsersGlobal', [
		'$q',
		'firebaseMeshedUsers',
		'projectNotifier',
		function ($q, firebaseMeshedUsers, projectNotifier: ProjectNotifier) {
			let projectId;
			let arr: any = [];
			let defer = $q.defer();

			update(projectId);
			projectNotifier.projectState.subscribe(function (state) {
				if (!state.projectId) {
					return;
				}
				if (projectId !== state.projectId) {
					update(state.projectId);
				}
			});

			function update(newProjectId) {
				projectId = newProjectId;
				if (!projectId) {
					if (arr.$destroy) {
						arr.$destroy();
					}
					return;
				}
				arr = new firebaseMeshedUsers(projectId);
				defer.resolve(arr);
			}

			return {
				getSync() {
					return arr;
				},
				getAsync() {
					//this is probably going to have issues long term...
					return defer.promise;
				},
			};
		},
	]);
// .run(['firebaseMeshedUsersGlobal', function(firebaseMeshedUsersGlobal) { window.meshGetter = firebaseMeshedUsersGlobal.getSync; }]); //this effectively just ensures that this service is init'd really early in the lifecycle
